import { Injectable } from "@angular/core";
import { Subject } from "rxjs";
import { WebBluetoothReceiptPrinter } from "./web-bluetooth-receipt-printer";
import EscPosEncoder from "esc-pos-encoder";
import * as mathjs from 'mathjs'
import { ToWords } from 'to-words';
import moment, { Moment } from "moment";
import { AppService } from "src/app/services/app.service";
import { THERMAL_PRINTER_WIDTH_PAPER } from "src/app/app.models";

declare let window: any;

export interface AndroidApi {
    scanBTDevices(): void;
    onBTDevicesScanned(_result: PlatformResult): Promise<any>;
    connectBTDevice(macAddress: string): Promise<DeviceInfos>;
    disconnectBTDevice(): void;
    printBTTicket(template: string, datas: string): Promise<any>;
}

@Injectable({
    providedIn: 'root'
})
export class BluetoothPrinterService {
    btPrinterDeviceConnected: any = null;
    bluetoothDevicesScanned = new Subject<any[]>();

    constructor(
        private _appService: AppService,
    ) { }

    // #region BT Functions
    scanBTDevices(): void {
        const androidApi: AndroidApi = window['AndroidApi'];
        window['AndroidApi'].onBTDevicesScanned = (_result: PlatformResult) => {
            if (_result.status == API_RESULT_STATUS.SUCCESS) {
                this.bluetoothDevicesScanned.next(_result.datas);
            }
        };
        androidApi.scanBTDevices();
    }

    connectBTDevice(macAddress: string) {
        return new Promise<DeviceInfos>((resolve, reject) => {
            window['AndroidApi'].onBtnDeviceConnection = (_result: PlatformResult) => {
                delete window['AndroidApi'].onBtnDeviceConnection;
                if (_result.status == API_RESULT_STATUS.SUCCESS) {
                    resolve(_result.datas);
                } else {
                    reject(_result.error);
                }
            };
            const androidApi: AndroidApi = window['AndroidApi'];
            androidApi.connectBTDevice(macAddress);
        });
    }

    tryReconnectBTDeviceFromWeb() {
        return new Promise<void>(async (resolve, reject) => {
            try {
                const previousDeviceId = localStorage.getItem('SS_LINKUP_WORKSPACE_BT_PRINTER_ID');
                const receiptPrinter = new WebBluetoothReceiptPrinter();
                receiptPrinter.addEventListener('connected', (device: any) => {
                    this.btPrinterDeviceConnected = receiptPrinter;
                    resolve();
                });
                await receiptPrinter.reconnect(previousDeviceId);
            } catch (error: any) {
                localStorage.removeItem('SS_LINKUP_WORKSPACE_BT_PRINTER_ID');
                reject(error);
            }
        });
    }

    connectBTDeviceFromWeb() {
        return new Promise<void>(async (resolve, reject) => {
            try {
                const receiptPrinter = new WebBluetoothReceiptPrinter();
                receiptPrinter.addEventListener('connected', (device: any) => {
                    localStorage.setItem('SS_LINKUP_WORKSPACE_BT_PRINTER_ID', device.id);
                    this.btPrinterDeviceConnected = receiptPrinter;
                    resolve();
                });
                await receiptPrinter.connect();
            } catch (error: any) {
                reject(error);
            }
        });
    }

    async disconnectBTDeviceFromWeb() {
        if (!!this.btPrinterDeviceConnected) {
            localStorage.removeItem('SS_LINKUP_WORKSPACE_BT_PRINTER_ID');
            await this.btPrinterDeviceConnected.disconnect();
        }
    }

    private formatPrintBTTicketItemText(text: string, colMaxLength: number, alignment: string) {
        let inputText = text.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        if (inputText.length > colMaxLength) {
            inputText = inputText.substring(0, colMaxLength);
        }

        let result = inputText.trim();
        switch (alignment) {
            case 'LEFT': {
                result = inputText + " ".padEnd(colMaxLength - inputText.length, ' ');
            } break;

            case 'CENTER': {
                const startPos = Math.floor(colMaxLength / 2) - Math.floor(inputText.length / 2);
                result = " ".padEnd(startPos, ' ') + inputText + " ".padEnd(colMaxLength - (startPos + inputText.length), ' ');
            } break;

            case 'RIGHT': {
                const startPos = colMaxLength - inputText.length;
                result = " ".padEnd(startPos, ' ') + inputText;
            } break;
        }

        return result;
    }

    private handleBTTicketItemAlignment(encoder: EscPosEncoder, item: any) {
        encoder = encoder.align('left');
        if (item.ALIGNMENT == 'CENTER') { encoder = encoder.align('center'); } else if (item.ALIGNMENT == 'RIGHT') { encoder = encoder.align('right'); }
        return encoder;
    }

    printBTTicketItemFromWeb(template: any, encoder: EscPosEncoder, item: any, datas: any, rowDatas?: any, rowDatasId?: string) {
        if (!!item.ITEMS && item.ITEMS.length > 0) {
            let paperSize = 30;
            if (!!template.PARAMS_MODELE.PAPER_SIZE) {
                switch (template.PARAMS_MODELE.PAPER_SIZE) {
                    case THERMAL_PRINTER_WIDTH_PAPER.PAPER_WIDTH_58MM:
                        paperSize = 30;
                        break;

                    case THERMAL_PRINTER_WIDTH_PAPER.PAPER_WIDTH_80MM:
                        paperSize = 47;
                        break;
                }
            }
            const colMaxLength = Math.floor(paperSize / item.ITEMS.length);
            let hasBreakline = false;
            item.ITEMS.forEach((col: any) => {
                if (!col.ITEMS || col.ITEMS.length == 0) {
                    col.ITEMS = [
                        { CODE: 'LABEL', DEFAULT_VALUE: '', ALIGNMENT: 'LEFT', TEXT_STYLE: 'NORMAL' }
                    ];
                }
                if (!!col.ITEMS) {
                    encoder = encoder.align('left').bold(false);
                    col.ITEMS.forEach((item: any) => {
                        let itemValue: string = !!item.BINDING ? item.BINDING : item.DEFAULT_VALUE;
                        if (!!itemValue) {
                            const regex = /\@\[([\d\w_.\|]+)\]/g;
                            const matchResults = itemValue.match(regex);
                            if (!!matchResults) {
                                matchResults.forEach(matchResult => {
                                    const matched: string = matchResult.substr(2, matchResult.length - 3);
                                    const bindingArray = matched.split('|')
                                    if (bindingArray.length == 3) {
                                        let matchResultValue = '';
                                        const typeId = bindingArray[0];
                                        const categoryId = bindingArray[1];
                                        const propertyId = bindingArray[2];

                                        if (categoryId !== rowDatasId && datas[categoryId] !== undefined && datas[categoryId].length > 0) {
                                            matchResultValue = datas[categoryId][0][propertyId] !== undefined ? datas[categoryId][0][propertyId] : '';
                                        } else if (!!rowDatas && rowDatas[propertyId] !== undefined) {
                                            matchResultValue = rowDatas[propertyId];
                                        }

                                        if (propertyId == 'CURRENT_DATE' && !!item.EXTRA_PROPERTIES) {
                                            const dateFormatAttr = item.EXTRA_PROPERTIES.find((i: any) => i.ID == 'DATE_FORMAT');
                                            if (!!dateFormatAttr?.DEFAULT_VALUE) {
                                                try {
                                                    const momentDate = datas['FUNCTION'][0]['CURRENT_DATE'] as Moment;
                                                    matchResultValue = momentDate.format(dateFormatAttr.DEFAULT_VALUE);
                                                } catch (e) { }
                                            }
                                        } else if (propertyId == 'FORMULA' && !!item.EXTRA_PROPERTIES) {
                                            const formulaExpressionAttr = item.EXTRA_PROPERTIES.find((i: any) => i.ID == 'FORMULA_EXPRESSION');
                                            if (!!formulaExpressionAttr?.DEFAULT_VALUE) {
                                                let formulatExpressionValue: string = formulaExpressionAttr.DEFAULT_VALUE;

                                                const formulaRegexMatches = formulatExpressionValue.match(regex);
                                                if (!!formulaRegexMatches) {
                                                    for (const formulaRegexMatch of formulaRegexMatches) {
                                                        const formulaRegexMatched: string = formulaRegexMatch.substr(2, formulaRegexMatch.length - 3);
                                                        const formulaBindingArray = formulaRegexMatched.split('|');
                                                        if (formulaBindingArray.length == 3) {
                                                            const formulaCategoryId = formulaBindingArray[1];
                                                            const formulaPropertyId = formulaBindingArray[2];
                                                            if (!!rowDatas && rowDatasId == formulaCategoryId && rowDatas[formulaPropertyId] !== undefined) {
                                                                formulatExpressionValue = formulatExpressionValue.replace(formulaRegexMatch, rowDatas[formulaPropertyId]);
                                                            } else if (!!datas[formulaCategoryId] && datas[formulaCategoryId].length > 0 && datas[formulaCategoryId][0][formulaPropertyId] !== undefined) {
                                                                formulatExpressionValue = formulatExpressionValue.replace(formulaRegexMatch, datas[formulaCategoryId][0][formulaPropertyId]);
                                                            }
                                                        }
                                                    }
                                                }

                                                try {
                                                    matchResultValue = mathjs.evaluate(formulatExpressionValue);
                                                } catch (e) { }
                                            }
                                        } else if (propertyId == 'NUMBER_TO_WORDS' && !!item.EXTRA_PROPERTIES) {
                                            const formulaExpressionAttr = item.EXTRA_PROPERTIES.find((i: any) => i.ID == 'FORMULA_EXPRESSION');
                                            if (!!formulaExpressionAttr?.DEFAULT_VALUE) {
                                                let formulatExpressionValue: string = formulaExpressionAttr.DEFAULT_VALUE;

                                                const formulaRegexMatches = formulatExpressionValue.match(regex);
                                                if (!!formulaRegexMatches) {
                                                    for (const formulaRegexMatch of formulaRegexMatches) {
                                                        const formulaRegexMatched: string = formulaRegexMatch.substr(2, formulaRegexMatch.length - 3);
                                                        const formulaBindingArray = formulaRegexMatched.split('|');
                                                        if (formulaBindingArray.length == 3) {
                                                            const formulaCategoryId = formulaBindingArray[1];
                                                            const formulaPropertyId = formulaBindingArray[2];
                                                            if (!!rowDatas && rowDatasId == formulaCategoryId && rowDatas[formulaPropertyId] !== undefined) {
                                                                formulatExpressionValue = formulatExpressionValue.replace(formulaRegexMatch, rowDatas[formulaPropertyId]);
                                                            } else if (!!datas[formulaCategoryId] && datas[formulaCategoryId].length > 0 && datas[formulaCategoryId][0][formulaPropertyId] !== undefined) {
                                                                formulatExpressionValue = formulatExpressionValue.replace(formulaRegexMatch, datas[formulaCategoryId][0][formulaPropertyId]);
                                                            }
                                                        }
                                                    }
                                                }

                                                try {
                                                    const evaluatedExpression = mathjs.evaluate(formulatExpressionValue);
                                                    const toWords = new ToWords({ localeCode: 'fr-FR' });
                                                    matchResultValue = toWords.convert(evaluatedExpression);
                                                } catch (e) { }
                                            }
                                        } else if (propertyId == 'TYPE_DOCUMENT' && !!item.EXTRA_PROPERTIES) {
                                            const typeDocumentAttr = item.EXTRA_PROPERTIES.find((i: any) => i.ID == 'TYPE_DOCUMENT');
                                            if (!!typeDocumentAttr?.DEFAULT_VALUE) {
                                                matchResultValue = typeDocumentAttr.DEFAULT_VALUE;
                                            }
                                        } else if (propertyId == 'TAXE' && !!item.EXTRA_PROPERTIES) {
                                            const taxAttr = item.EXTRA_PROPERTIES.find((i: any) => i.ID == 'TAX_ID');
                                            if (!!taxAttr?.DEFAULT_VALUE) {
                                                const taxId = taxAttr.DEFAULT_VALUE;
                                                const categoryTaxesId = 'TAXES';
                                                if (!!datas[categoryTaxesId] && datas[categoryTaxesId].length > 0) {
                                                    const matchedTax = datas[categoryTaxesId].find((d: any) => d.DL_CODETAXE == taxId);
                                                    if (!!matchedTax) {
                                                        matchResultValue = matchedTax.T_MONTANT;
                                                    }
                                                }
                                            }
                                        }
                                        itemValue = itemValue.replaceAll(matchResult, matchResultValue);
                                    }
                                });
                            }
                        }
                        if (itemValue !== undefined) {
                            switch (item.CODE) {
                                case 'LABEL':
                                    let fncResult = itemValue;
                                    const regex = /([A-Z_]+)\(([^)]+)\)/g;
                                    let match;
                                    while ((match = regex.exec(itemValue)) !== null) {
                                        const functionId = match[1];
                                        switch(functionId) {
                                            case 'DATE_FORMAT':
                                                const fncParams = match[2].split(',');
                                                if (fncParams.length == 2) {
                                                    const dateFormat = fncParams[1].replaceAll("'", "").replaceAll('"', "");
                                                    const formatedDate = moment(fncParams[0]).format(dateFormat);
                                                    fncResult = fncResult.replaceAll(match[0], formatedDate);
                                                }
                                                if (!isNaN(match[2] as any)) {
                                                    const formatedNumber = parseFloat(match[2]).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
                                                    fncResult = fncResult.replaceAll(match[0], formatedNumber);
                                                }
                                                break;

                                            case 'NUMBER_FORMAT':
                                                if (!isNaN(match[2] as any)) {
                                                    const formatedNumber = parseFloat(match[2]).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " ").replace('.', ',');
                                                    fncResult = fncResult.replaceAll(match[0], formatedNumber);
                                                }
                                                break;
                                        }
                                    }
                                    itemValue = fncResult;

                                    const maxLength: number = !isNaN(item.MAX_LENGTH) ? parseInt(item.MAX_LENGTH) : -1;
                                    if (maxLength > 0) { itemValue = itemValue.substring(0, maxLength); }
                                    let textContent = this.formatPrintBTTicketItemText(itemValue + '', colMaxLength, col.ALIGNMENT);
                                    if (item.TEXT_STYLE == 'BOLD') { encoder = encoder.bold(true); }
                                    if (!!item.ITEMS && col.ORIENTATION == "COLUMN" && item.ITEMS.length == 1) { encoder = encoder.newline(); hasBreakline = true }
                                    encoder = encoder.text(textContent);
                                    break;

                                case 'H_SEPARATOR':
                                    encoder = encoder.text('-'.repeat(colMaxLength));
                                    break;

                                case 'BARCODE':
                                    encoder = this.handleBTTicketItemAlignment(encoder, col);
                                    const symbology = !!item.SYMBOLOGY_TYPE ? item.SYMBOLOGY_TYPE : 'code128';
                                    encoder = encoder.barcode(itemValue, symbology, 120);
                                    break;

                                case 'IMAGE':
                                    let img = new Image();
                                    img.src = itemValue;
                                    encoder = this.handleBTTicketItemAlignment(encoder, col);
                                    encoder = encoder.image(img, 192, 192)
                                    break;

                                case 'QRCODE':
                                    encoder = this.handleBTTicketItemAlignment(encoder, col);
                                    encoder = encoder.qrcode(itemValue);
                                    break;
                            }
                        }
                    });
                }
            });
            if (!hasBreakline) {
                encoder = encoder.newline();
            }
        }

        return encoder;
    }

    printBTTicketRowFromWeb(template: any, encoder: EscPosEncoder, row: any, datas: any, rowDatas?: any, rowDatasId?: string) {
        encoder = this.printBTTicketItemFromWeb(template, encoder, row, datas, rowDatas, rowDatasId);
    }

    printBTTicketTemplateFromWeb(template: any, encoder: EscPosEncoder, datas: any, item: any) {
        if (!item.ITEMS) { item.ITEMS = []; }
        if (item.CODE == 'ROW') {
            this.printBTTicketRowFromWeb(template, encoder, item, datas);
        } else if (item.CODE == 'SECTION') {
            if (!!item.BINDING) {
                if (!!datas[item.BINDING]) {
                    datas[item.BINDING].forEach((rowBinding: any) => {
                        item.ITEMS.forEach((i: any) => {
                            this.printBTTicketRowFromWeb(template, encoder, i, datas, rowBinding, item.BINDING);
                        });
                    });
                }
            } else {
                item.ITEMS.forEach((i: any) => {
                    this.printBTTicketRowFromWeb(template, encoder, i, datas);
                });
            }
        }
    }

    async printBTTicketFromWeb(template: any, datas: any): Promise<void> {
        if (!this.btPrinterDeviceConnected) { throw "Aucune imprimante bluetooth connectée"; }
        datas['FUNCTION'] = [
            {
                'CURRENT_DATE': moment(),
                'APP_VERSION': this._appService.appVersion$.value.APP_VERSION_ID,
            }
        ];
        let encoder = new EscPosEncoder().initialize();
        template.PARAMS_MODELE.LAYOUT.forEach((item: any) => {
            this.printBTTicketTemplateFromWeb(template, encoder, datas, item);
        });
        encoder = encoder.newline().newline().newline().cut();
        const data = encoder.encode();

        if (this._appService.isMobileApp) {
            const androidApi: any = window['AndroidApi'];
            androidApi.testBtPrint(data);
        } else {
            await this.btPrinterDeviceConnected.print(data);
        }
    }

    disconnectBTDevice() {
        const androidApi: AndroidApi = window['AndroidApi'];
        androidApi.disconnectBTDevice();
    }

    printBTTicket(template: any, datas: any) {
        return new Promise<DeviceInfos>((resolve, reject) => {
            window['AndroidApi'].onPrintBTTicket = (_result: PlatformResult) => {
                delete window['AndroidApi'].onPrintBTTicket;
                if (_result.status == API_RESULT_STATUS.SUCCESS) {
                    resolve(_result.datas);
                } else {
                    reject(_result.error);
                }
            };
            const androidApi: AndroidApi = window['AndroidApi'];
            androidApi.printBTTicket(JSON.stringify(template), JSON.stringify(datas));
        });
    }
    // #endregion
}

// MODELS
export class DeviceInfos {
    marque: string;
    modele: string;
    macAddress: string;
    androidID: string;
    appVersion: string;
}



export class PlatformResult {
    status: API_RESULT_STATUS;
    datas?: any;
    error?: string;
}

export enum API_RESULT_STATUS {
    ERROR = -1,
    PENDING = 0,
    SUCCESS = 1
}
